import { ScrollView, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { PillButton } from '../../components/PillButton'
import { useKeyboardHeight } from '../../flow/useKeyboardHeight'
import { useFlowStepFrame } from '../../hooks/useNestedSheetHeight'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../../theme/restyle'
import { FiatBackButton } from '../FiatBackButton'
import { KYC_FORM_METRICS } from '../kycFormTheme'

export interface StripeSessionAuthScreenProps {
  verifiedEmail: string
  isSubmitting: boolean
  errorMessage?: string
  onContinue: () => void
  onClose: () => void
}

/** Fun-owned Link preflight UI, kept inside Stripe's `SESSION_AUTH` executor. */
export function StripeSessionAuthScreen({
  verifiedEmail,
  isSubmitting,
  errorMessage,
  onContinue,
  onClose,
}: StripeSessionAuthScreenProps) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  // A flow step, not a nested sheet: without the inset the back disc draws
  // under the status bar and the screen ends one inset short of the sheet.
  const { height, paddingTop } = useFlowStepFrame()
  const insets = useSafeAreaInsets()
  const keyboardHeight = useKeyboardHeight(true)
  const actionLabel = isSubmitting ? t('fiat.submitting') : t('common.continue')

  return (
    <Box
      style={{
        height,
        paddingTop,
        paddingBottom: keyboardHeight,
        backgroundColor: theme.background.modal,
      }}
    >
      <FiatBackButton onPress={onClose} />
      <ScrollView
        style={styles.scroll}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[
          styles.content,
          {
            paddingBottom:
              KYC_FORM_METRICS.advanceGap +
              (keyboardHeight > 0 ? 0 : insets.bottom),
          },
        ]}
        showsVerticalScrollIndicator={false}
      >
        <Box style={styles.heading}>
          <Text variant="drawerTitle">{t('fiat.stripeAuthTitle')}</Text>
          <Text
            style={[styles.detail, { color: theme.content.elements.secondary }]}
          >
            {t('fiat.stripeAuthDetail', { email: verifiedEmail })}
          </Text>
        </Box>

        {errorMessage === undefined ? null : (
          <Text
            variant="detail"
            style={[styles.error, { color: theme.content.elements.error }]}
          >
            {errorMessage}
          </Text>
        )}

        <Box style={styles.action}>
          <PillButton
            label={actionLabel}
            disabled={isSubmitting}
            onPress={onContinue}
          />
        </Box>
      </ScrollView>
    </Box>
  )
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  content: {
    flexGrow: 1,
    paddingTop: KYC_FORM_METRICS.headingTop,
    paddingHorizontal: KYC_FORM_METRICS.screenInset,
    gap: KYC_FORM_METRICS.advanceGap,
  },
  heading: {
    paddingHorizontal:
      KYC_FORM_METRICS.headingInset - KYC_FORM_METRICS.screenInset,
    gap: KYC_FORM_METRICS.headingGap,
  },
  detail: {
    fontSize: KYC_FORM_METRICS.detailFontSize,
    lineHeight: KYC_FORM_METRICS.detailLineHeight,
  },
  error: { paddingHorizontal: KYC_FORM_METRICS.fieldPaddingHorizontal },
  action: { marginTop: 'auto' },
})
